import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "@/lib/stores/auth";
import { useBook } from "@/lib/api/books";
import { ChapterHeader } from "@/components/book/chapter-header";
import { ChapterContent } from "@/components/book/chapter-content";
import { ChapterNav } from "@/components/book/chapter-nav";
import { Loader2Icon } from "lucide-react";

export function ChapterPage() {
  const navigate = useNavigate();
  const { id, order: orderParam } = useParams<{ id: string; order: string }>();
  const bookId = Number(id);
  const order = Number(orderParam);

  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { data: book, isLoading: bookLoading } = useBook(bookId);

  // Auth guard
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Loading
  if (authLoading || bookLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Book not found
  if (!book) {
    navigate("/dashboard");
    return null;
  }

  // Find chapter by order
  const chapter = book.chapters.find((ch) => ch.order === order);
  if (!chapter) {
    navigate(`/books/${bookId}`);
    return null;
  }

  const completedCount = book.progress?.last_completed_chapter_order ?? 0;

  return (
    <div className="space-y-6">
      <ChapterHeader
        bookId={bookId}
        bookTitle={book.title}
        order={order}
        total={book.total_chapters}
        completedCount={completedCount}
      />

      <ChapterContent chapter={chapter} />

      <div className="border-t" />

      <ChapterNav
        bookId={bookId}
        chapters={book.chapters}
        currentOrder={order}
      />
    </div>
  );
}
